import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { usd, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Landmark, Check, X, ArrowDownCircle, ArrowUpCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ReceiptCapture from "@/components/ReceiptCapture";

const COST_CATEGORIES = [
  "Materials", "Labor", "Subcontractor", "Permits & Fees",
  "Equipment & Rental", "Fuel & Travel", "Office & Overhead", "Other",
];

export default function BankInbox() {
  const qc = useQueryClient();
  const [simAmount, setSimAmount] = useState("");
  const [simDir, setSimDir] = useState("credit");
  const [pickJob, setPickJob] = useState({}); // txId -> jobId override
  const [pickCat, setPickCat] = useState({}); // txId -> cost category override

  const { data, isLoading } = useQuery({
    queryKey: ["bank-transactions"],
    queryFn: async () => (await api.get("/bank/transactions")).data,
  });
  const { data: jobsData } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => (await api.get("/jobs")).data,
  });
  const jobs = Array.isArray(jobsData) ? jobsData : (jobsData?.jobs || []);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["bank-transactions"] });
    qc.invalidateQueries({ queryKey: ["financials-overview"] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
  };

  const inject = useMutation({
    mutationFn: async () => (await api.post("/bank/transactions/test-inject", {
      amount: Number(simAmount),
      direction: simDir,
      name: simDir === "credit" ? "Simulated deposit" : "Simulated purchase",
    })).data,
    onSuccess: () => { setSimAmount(""); toast.success("Test transaction added"); refresh(); },
    onError: async (e) => toast.error(await formatApiError(e, "Could not add test transaction.")),
  });

  const confirmDeposit = useMutation({
    mutationFn: async ({ txId, jobId }) => (await api.post(`/bank/transactions/${txId}/confirm-deposit`, { job_id: jobId || null })).data,
    onSuccess: () => { toast.success("Deposit marked collected"); refresh(); },
    onError: async (e) => toast.error(await formatApiError(e, "Could not confirm the deposit.")),
  });
  const dismiss = useMutation({
    mutationFn: async (txId) => (await api.post(`/bank/transactions/${txId}/dismiss`, {})).data,
    onSuccess: () => { toast.success("Dismissed"); refresh(); },
    onError: async (e) => toast.error(await formatApiError(e, "Could not dismiss.")),
  });
  const assign = useMutation({
    mutationFn: async ({ txId, kind, jobId, costCategory }) => (await api.post(`/bank/transactions/${txId}/assign`, { kind, job_id: jobId || null, cost_category: costCategory || null })).data,
    onSuccess: () => { toast.success("Saved"); refresh(); },
    onError: async (e) => toast.error(await formatApiError(e, "Could not save.")),
  });

  const deposits = data?.deposits_to_review || [];
  const purchases = data?.purchases_to_review || [];
  const handled = (data?.transactions || []).filter((t) => !["unreviewed", "suggested"].includes(t.status));

  return (
    <div className="space-y-6" data-testid="bank-inbox">
      <ReceiptCapture />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[#0B3A8F] font-semibold"><Landmark size={18} /> Bank activity</div>
        <p className="mt-1 text-sm text-[#4B6370]">
          When your checking account is connected through Plaid, deposits and purchases land here automatically.
          A deposit that matches the 40% down on a job is flagged so you can confirm it in one tap — just like reconciling in QuickBooks.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3">
          <div className="flex items-center gap-1 text-xs font-semibold text-[#4B6370]"><Sparkles size={14} /> Try it now (test):</div>
          <Input type="number" placeholder="Amount" value={simAmount} onChange={(e) => setSimAmount(e.target.value)} className="h-9 w-32" data-testid="bank-sim-amount" />
          <Select value={simDir} onValueChange={setSimDir}>
            <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="credit">Deposit (money in)</SelectItem>
              <SelectItem value="debit">Purchase (money out)</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="h-9 bg-[#0B3A8F]" disabled={!simAmount || inject.isPending} onClick={() => inject.mutate()} data-testid="bank-sim-add">
            Add test transaction
          </Button>
        </div>
      </div>

      {isLoading && <div className="text-[#4B6370]">Loading bank activity…</div>}

      <div>
        <h3 className="mb-2 flex items-center gap-2 font-semibold text-[#061A23]"><ArrowDownCircle size={18} className="text-emerald-600" /> Deposits to review</h3>
        {deposits.length === 0 && <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-[#4B6370]">No deposits waiting. Matched deposits appear here.</div>}
        <div className="space-y-3">
          {deposits.map((tx) => {
            const s = tx.suggestion;
            const jobId = pickJob[tx.id] ?? (s?.job_id || "");
            return (
              <div key={tx.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`deposit-card-${tx.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-lg font-bold text-[#061A23]">{usd(tx.amount)}</div>
                  <div className="text-xs text-[#4B6370]">{fmtDate(tx.posted_date)} · {tx.name}</div>
                </div>
                {s ? (
                  <p className="mt-2 text-sm text-[#061A23]">
                    Is this the <b>deposit</b> for <b>{s.client_name}</b> — {s.estimate_number} (Job {s.job_number})?
                    <span className="text-[#4B6370]"> Expected 40% down: {usd(s.expected_deposit)}.</span>
                  </p>
                ) : (
                  <div className="mt-2">
                    <p className="text-sm text-[#4B6370]">No automatic match. Pick the job this deposit belongs to:</p>
                    <Select value={jobId} onValueChange={(v) => setPickJob((p) => ({ ...p, [tx.id]: v }))}>
                      <SelectTrigger className="mt-1 h-9 w-full sm:w-80"><SelectValue placeholder="Choose a job" /></SelectTrigger>
                      <SelectContent>
                        {jobs.map((j) => (
                          <SelectItem key={j.id} value={j.id}>{(j.job_number || "Job")} — {j.client_name || j.title || ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700" disabled={!jobId || confirmDeposit.isPending} onClick={() => confirmDeposit.mutate({ txId: tx.id, jobId })} data-testid={`deposit-confirm-${tx.id}`}>
                    <Check size={15} className="mr-1" /> Yes — mark collected
                  </Button>
                  <Button size="sm" variant="outline" className="h-9" disabled={dismiss.isPending} onClick={() => dismiss.mutate(tx.id)} data-testid={`deposit-dismiss-${tx.id}`}>
                    <X size={15} className="mr-1" /> Not this
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="mb-2 flex items-center gap-2 font-semibold text-[#061A23]"><ArrowUpCircle size={18} className="text-[#C45C26]" /> Purchases to review</h3>
        {purchases.length === 0 && <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-[#4B6370]">No purchases waiting.</div>}
        <div className="space-y-3">
          {purchases.map((tx) => {
            const sug = tx.category_suggestion || {};
            const cat = pickCat[tx.id] ?? (sug.cost_category || "Materials");
            return (
              <div key={tx.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`purchase-card-${tx.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-lg font-bold text-[#061A23]">{usd(tx.amount)}</div>
                  <div className="text-xs text-[#4B6370]">{fmtDate(tx.posted_date)} · {tx.name}</div>
                </div>
                {sug.cost_category && (
                  <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-[#4B6370]">
                    <span className="font-semibold text-[#0B3A8F]">AI suggests:</span>{" "}
                    {sug.cost_category} · {sug.tax_deductible ? "✅ tax write-off" : "⛔ not deductible"}
                    {sug.tax_category ? ` · files under "${sug.tax_category}"` : ""}
                    {sug.rationale ? <div className="mt-0.5 italic">{sug.rationale}</div> : null}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Select value={cat} onValueChange={(v) => setPickCat((p) => ({ ...p, [tx.id]: v }))}>
                    <SelectTrigger className="h-9 w-full sm:w-48" data-testid={`purchase-cat-${tx.id}`}><SelectValue placeholder="Cost category" /></SelectTrigger>
                    <SelectContent>
                      {COST_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={pickJob[tx.id] || ""} onValueChange={(v) => setPickJob((p) => ({ ...p, [tx.id]: v }))}>
                    <SelectTrigger className="h-9 w-full sm:w-60"><SelectValue placeholder="Assign to a job…" /></SelectTrigger>
                    <SelectContent>
                      {jobs.map((j) => (
                        <SelectItem key={j.id} value={j.id}>{(j.job_number || "Job")} — {j.client_name || j.title || ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="h-9 bg-[#0B3A8F]" disabled={!pickJob[tx.id] || assign.isPending} onClick={() => assign.mutate({ txId: tx.id, kind: "job", jobId: pickJob[tx.id], costCategory: cat })} data-testid={`purchase-assign-${tx.id}`}>
                    Assign as job expense
                  </Button>
                  <Button size="sm" variant="outline" className="h-9" disabled={assign.isPending} onClick={() => assign.mutate({ txId: tx.id, kind: "personal" })} data-testid={`purchase-personal-${tx.id}`}>
                    Deny — personal
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {handled.length > 0 && (
        <div>
          <h3 className="mb-2 font-semibold text-[#061A23]">Recently handled</h3>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {handled.slice(0, 15).map((tx) => (
              <div key={tx.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className="text-[#061A23]">{tx.direction === "credit" ? "+" : "−"}{usd(tx.amount)} · {tx.name}</span>
                <span className="text-xs font-semibold uppercase text-[#4B6370]">{String(tx.status || "").replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
